import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Camera, Film, Package, Check, ArrowUpRight, Globe, X } from "lucide-react";
import { resolveImageUrl } from "../utils/image";
import SEO from "../components/SEO";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Reveal({ children, delay = 0, direction = "up" }) {
  const variants = {
    hidden: { opacity: 0, y: direction === "up" ? 40 : 0, x: direction === "left" ? 40 : direction === "right" ? -40 : 0 },
    visible: { opacity: 1, y: 0, x: 0 },
  };
  return (
    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
      variants={variants} transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.div>
  );
}

const DEFAULTS = {
  services_hero_label: "Services",
  services_hero_title: "Visual Solutions That Drive Results",
  services_hero_desc: "From single-project shoots to ongoing content partnerships, we offer flexible solutions designed to meet your business objectives.",
  services_photo_title: "Commercial Photography",
  services_photo_desc: "Strategic photography that captures attention and communicates your brand's value proposition with clarity and impact.",
  services_photo_1_title: "Branding Shoots",
  services_photo_1_desc: "Comprehensive visual identity packages that define your brand aesthetic and create cohesive imagery across all touchpoints.",
  services_photo_2_title: "Corporate Portraits",
  services_photo_2_desc: "Professional headshots and team photography that convey authority, approachability, and brand values.",
  services_photo_3_title: "Product Photography",
  services_photo_3_desc: "High-impact product imagery that showcases your offerings in their best light and drives conversions.",
  services_photo_4_title: "Campaign Visuals",
  services_photo_4_desc: "Strategic campaign photography designed to support marketing initiatives and achieve specific business objectives.",
  services_video_title: "Commercial Videography",
  services_video_desc: "Cinematic video content that tells your story with impact and drives meaningful engagement with your audience.",
  services_video_1_title: "Brand Films",
  services_video_1_desc: "Cinematic storytelling that captures your brand's essence, values, and vision in a compelling narrative format.",
  services_video_2_title: "Promotional Videos",
  services_video_2_desc: "Conversion-focused video content designed to showcase products, services, and drive customer action.",
  services_video_3_title: "Social Media Content",
  services_video_3_desc: "Platform-optimized video content that engages audiences and builds brand presence across social channels.",
  services_video_4_title: "Event Coverage",
  services_video_4_desc: "Professional documentation of corporate events, conferences, and brand activations.",
  services_pkg_title: "Content Subscription Packages",
  services_pkg_desc: "Ongoing content creation with predictable pricing. Monthly retainers that keep your brand consistently visible.",
  services_pkg_1_name: "Starter",
  services_pkg_1_desc: "Perfect for businesses beginning their visual content journey",
  services_pkg_1_features: "2 Video reels\n30 secs duration each\n2 hrs video shoot\n20x pro edited photos\nPro equipment included",
  services_pkg_2_name: "Growth",
  services_pkg_2_desc: "Ideal for brands ready to scale their content output",
  services_pkg_2_features: "4 Video reels\n30 secs duration each\n4 hrs video shoot\n40x pro edited photos\nPro equipment included",
  services_pkg_3_name: "Enterprise",
  services_pkg_3_desc: "Comprehensive content partnership for established brands",
  services_pkg_3_features: "7 Video reels\n30 secs duration each\n8 hrs video shoot\n80x pro edited photos\nPro equipment included",
  services_notes_title: "What's Included",
  services_note_1_title: "Content Creation & Publishing",
  services_note_1_desc: "All packages include professional content creation, editing, captions, basic hashtags, scheduling, and publishing according to your selected plan.",
  services_note_2_title: "Platforms",
  services_note_2_desc: "Retainers cover Facebook and Instagram. The same approved content may be published across both platforms, with platform-specific content available by separate arrangement.",
  services_note_3_title: "What's Not Included",
  services_note_3_desc: "Community management, replying to comments and direct messages, handling customer enquiries, paid advertising, and ad campaign management are not included in the monthly retainer.",
  services_process_title: "How We Work",
  services_step_1_title: "Discovery",
  services_step_1_desc: "We learn about your brand, objectives, and vision through an in-depth consultation.",
  services_step_2_title: "Strategy",
  services_step_2_desc: "We develop a creative approach tailored to your goals and target audience.",
  services_step_3_title: "Production",
  services_step_3_desc: "We execute with precision, capturing content that aligns with our shared vision.",
  services_step_4_title: "Delivery",
  services_step_4_desc: "We refine and deliver polished assets ready to drive results for your business.",
  services_cta_title: "Let's Discuss Your Project",
  services_cta_desc: "Every project starts with a conversation. Tell us about your vision and let's explore how we can bring it to life.",
};

const DEFAULT_PHOTO_IMAGE = "https://images.unsplash.com/photo-1766934587163-186d20bf3d40?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwZmFzaGlvbiUyMHBob3RvZ3JhcGh5JTIwc3R1ZGlvJTIwbGlnaHRpbmclMjBoaWdoJTIwZW5kfGVufDB8fHx8MTc3MTYyMDgxMHww&ixlib=rb-4.1.0&q=85";
const DEFAULT_VIDEO_IMAGE = "/api/images/11dfec5b-c3d0-4458-b5c9-bb4db849dce0";

export default function Services() {
  const [content, setContent] = useState({});
  const [photoImage, setPhotoImage] = useState(DEFAULT_PHOTO_IMAGE);
  const [videoImage, setVideoImage] = useState(DEFAULT_VIDEO_IMAGE);

  const c = (key) => content[key] || DEFAULTS[key] || "";

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/site-content`),
      axios.get(`${API}/site-settings`)
    ]).then(([contentRes, settingsRes]) => {
      setContent(contentRes.data || {});
      if (settingsRes.data.services_photo_image) setPhotoImage(settingsRes.data.services_photo_image);
      if (settingsRes.data.services_video_image) setVideoImage(settingsRes.data.services_video_image);
    }).catch(() => {});
  }, []);

  const getFeatures = (key) => c(key).split("\n").filter(Boolean);
  const pkgHighlight = [false, true, false];
  return (
    <div data-testid="services-page" className="pt-40">
      <SEO title="Photography, Videography & Social Media Content Services" description="Commercial photography, video production and monthly social media content packages for businesses in Manawatū, Palmerston North and Wellington." path="/services" />

      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <motion.span initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="text-[#D4C5B0] text-sm uppercase tracking-[0.3em] font-medium block">
              {c("services_hero_label")}
            </motion.span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#EDEDED] mt-4 mb-6 leading-tight">{c("services_hero_title")}</h1>
            <p className="text-[#A3A3A3] text-lg leading-relaxed">{c("services_hero_desc")}</p>
          </motion.div>
        </div>
      </section>

      {/* Photography Section */}
      <section id="photography" data-testid="photography-section" className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="sticky top-32">
              <div className="flex items-center gap-4 mb-6">
                <Camera className="w-10 h-10 text-[#D4C5B0]" />
                <span className="text-[#D4C5B0] text-sm uppercase tracking-[0.3em] font-medium">Photography</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#EDEDED] mb-6">{c("services_photo_title")}</h2>
              <p className="text-[#A3A3A3] text-lg leading-relaxed mb-8">{c("services_photo_desc")}</p>
              <div className="aspect-[4/3] bg-[#121212] overflow-hidden">
                <img src={resolveImageUrl(photoImage)} alt="Commercial photographer in Manawatū and Wellington – OBRA Creatives" className="w-full h-full object-cover" />
              </div>
            </motion.div>
            <div className="space-y-6">
              {[1, 2, 3, 4].map((i, index) => (
                <Reveal key={i} delay={index * 0.1}>
                  <motion.div whileHover={{ x: 8, borderColor: "#D4C5B0" }} transition={{ duration: 0.3 }}
                    data-testid={`photography-service-${index}`} className="bg-[#121212] border border-[#262626] p-8 transition-shadow hover:shadow-lg hover:shadow-[#D4C5B0]/5">
                    <h3 className="text-xl font-bold text-[#EDEDED] mb-3">{c(`services_photo_${i}_title`)}</h3>
                    <p className="text-[#A3A3A3] leading-relaxed">{c(`services_photo_${i}_desc`)}</p>
                  </motion.div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Videography Section */}
      <section id="videography" data-testid="videography-section" className="py-24 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div className="space-y-6 order-2 lg:order-1">
              {[1, 2, 3, 4].map((i, index) => (
                <Reveal key={i} delay={index * 0.1}>
                  <motion.div whileHover={{ x: 8, borderColor: "#D4C5B0" }} transition={{ duration: 0.3 }}
                    data-testid={`videography-service-${index}`} className="bg-[#121212] border border-[#262626] p-8 transition-shadow hover:shadow-lg hover:shadow-[#D4C5B0]/5">
                    <h3 className="text-xl font-bold text-[#EDEDED] mb-3">{c(`services_video_${i}_title`)}</h3>
                    <p className="text-[#A3A3A3] leading-relaxed">{c(`services_video_${i}_desc`)}</p>
                  </motion.div>
                </Reveal>
              ))}
            </div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="sticky top-32 order-1 lg:order-2">
              <div className="flex items-center gap-4 mb-6">
                <Film className="w-10 h-10 text-[#D4C5B0]" />
                <span className="text-[#D4C5B0] text-sm uppercase tracking-[0.3em] font-medium">Videography</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#EDEDED] mb-6">{c("services_video_title")}</h2>
              <p className="text-[#A3A3A3] text-lg leading-relaxed mb-8">{c("services_video_desc")}</p>
              <div className="aspect-[4/3] bg-[#121212] overflow-hidden">
                <img src={resolveImageUrl(videoImage)} alt="Videographer and video production in Manawatū and Wellington – OBRA Creatives" className="w-full h-full object-cover" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content Packages Section */}
      <section id="packages" data-testid="packages-section" className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-16">
            <div className="flex items-center justify-center gap-4 mb-6">
              <Package className="w-10 h-10 text-[#D4C5B0]" />
              <span className="text-[#D4C5B0] text-sm uppercase tracking-[0.3em] font-medium">Retainers</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#EDEDED] mb-6">{c("services_pkg_title")}</h2>
            <p className="text-[#A3A3A3] text-lg max-w-2xl mx-auto">{c("services_pkg_desc")}</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((i, index) => {
              const highlight = pkgHighlight[index];
              return (
                <Reveal key={i} delay={index * 0.12}>
                  <motion.div whileHover={{ y: -8, scale: 1.02 }} transition={{ duration: 0.3 }}
                    data-testid={`package-${index}`} className={`p-8 h-full ${highlight ? "bg-[#D4C5B0] text-[#050505]" : "bg-[#121212] border border-[#262626]"}`}>
                    <h3 className={`text-2xl font-bold mb-2 ${highlight ? "text-[#050505]" : "text-[#EDEDED]"}`}>{c(`services_pkg_${i}_name`)}</h3>
                    <p className={`mb-8 ${highlight ? "text-[#050505]/70" : "text-[#A3A3A3]"}`}>{c(`services_pkg_${i}_desc`)}</p>
                    <ul className="space-y-4 mb-8">
                      {getFeatures(`services_pkg_${i}_features`).map((feature, fi) => (
                        <li key={fi} className="flex items-start gap-3">
                          <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${highlight ? "text-[#050505]" : "text-[#D4C5B0]"}`} />
                          <span className={highlight ? "text-[#050505]" : "text-[#EDEDED]"}>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to="/contact" className={`inline-flex items-center justify-center gap-2 w-full py-4 font-semibold text-sm uppercase tracking-wider transition-all ${highlight ? "bg-[#050505] text-[#EDEDED] hover:bg-[#1E1E1E]" : "btn-secondary"}`}>
                      Get Started <ArrowUpRight size={16} />
                    </Link>
                  </motion.div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.1}>
            <div data-testid="packages-notes" className="mt-16 border border-[#262626] bg-[#0F0F0F] p-8 md:p-12">
              <span className="text-[#D4C5B0] text-sm uppercase tracking-[0.3em] font-medium">{c("services_notes_title")}</span>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mt-8">
                {[[1, Check], [2, Globe], [3, X]].map(([i, Icon]) => (
                  <div key={i} data-testid={`package-note-${i}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <Icon className="w-5 h-5 text-[#D4C5B0] flex-shrink-0" />
                      <h3 className="text-lg font-bold text-[#EDEDED]">{c(`services_note_${i}_title`)}</h3>
                    </div>
                    <p className="text-[#A3A3A3] text-sm leading-relaxed">{c(`services_note_${i}_desc`)}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Process Section */}
      <section data-testid="process-section" className="py-24 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-16">
            <span className="text-[#D4C5B0] text-sm uppercase tracking-[0.3em] font-medium">Our Process</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#EDEDED] mt-4">{c("services_process_title")}</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[1, 2, 3, 4].map((i, index) => (
              <Reveal key={i} delay={index * 0.12}>
                <div className="text-center group">
                  <motion.div whileHover={{ scale: 1.1 }} transition={{ type: "spring", stiffness: 300 }} className="text-5xl font-bold text-[#D4C5B0] mb-4">
                    {String(i).padStart(2, "0")}
                  </motion.div>
                  <h3 className="text-xl font-bold text-[#EDEDED] mb-3">{c(`services_step_${i}_title`)}</h3>
                  <p className="text-[#A3A3A3]">{c(`services_step_${i}_desc`)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A] border-t border-[#262626]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#EDEDED] mb-6">{c("services_cta_title")}</h2>
            <p className="text-[#A3A3A3] text-lg mb-10 max-w-2xl mx-auto">{c("services_cta_desc")}</p>
            <Link to="/contact" data-testid="services-cta-button" className="inline-flex items-center gap-2 btn-primary text-sm uppercase tracking-wider">
              Book a Consultation <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
